import { useGeneSearchOptions } from "@api/generated/gene-search/gene-search";
import type { TermIn, TermInKind, TermInWant } from "@api/generated/model";
import { hint } from "@i18n";
import { Button, Popover, SegmentedControl, Select, TextInput } from "@mantine/core";
import { RemovableChip } from "@ui/RemovableChip";
import type { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const KINDS = ["locus", "allele", "marker"] as const satisfies readonly TermInKind[];
const WANTS = ["present", "absent", "value"] as const satisfies readonly TermInWant[];

export const termSchema = z.object({ kind: z.enum(KINDS), name: z.string(), want: z.enum(WANTS), value: z.string().nullable().optional() });
export type Term = z.infer<typeof termSchema>; // a term as a link carries it (every field set)

export const combineSchema = z.enum(["all", "any"]);
export type Combine = z.infer<typeof combineSchema>; // every term (AND), or at least one (OR)

export const termKey = (t: TermIn) => `${t.kind}|${t.want}|${t.value ?? ""}|${t.name}`;

export function termLabel(t: TFunction, term: TermIn) {
  return term.want === "value" ? `${term.name} = ${term.value ?? ""}` : `${term.name} · ${t(`geneSearch.want.${term.want ?? "present"}`)}`;
}

/** Gene terms (a locus, an allele or a marker: present, absent, a matrix value) as chips joined by AND or OR (the
 *  legacy "Multiple Gene / Allele selection"), one added from a small form (Génkereső, Felmenők keresése). */
export function TermChips(props: {
  terms: Term[];
  onChange: (terms: Term[]) => void;
  combine?: Combine;
  onCombine?: (combine: Combine) => void;
}) {
  const { t } = useTranslation();
  const options = useGeneSearchOptions({ query: { staleTime: Number.POSITIVE_INFINITY } }).data;
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<Term["kind"]>("locus");
  const [name, setName] = useState<string | null>(null);
  const [chosen, setWant] = useState<Term["want"]>("present");
  const [value, setValue] = useState("");
  const want = chosen === "value" && kind !== "locus" ? "present" : chosen; // a value is a Genes-matrix locus's only
  const names = options ? { locus: options.loci, allele: options.alleles, marker: options.markers }[kind] : [];
  const ready = !!name && (want !== "value" || !!value.trim());
  const add = () => {
    if (!name || !ready) return;
    props.onChange([...props.terms, { kind, name, want, value: want === "value" ? value.trim() : null }]);
    setOpen(false);
    setName(null);
    setValue("");
  };
  const combine = props.combine ?? "all";
  return (
    <div className="chips">
      {props.onCombine && props.terms.length > 1 && (
        <SegmentedControl
          size="xs"
          value={combine}
          onChange={(v) => props.onCombine?.(v as Combine)}
          aria-label={t("geneSearch.conditionLogic")}
          data={[
            { value: "all", label: t("geneSearch.matchAll") },
            { value: "any", label: t("geneSearch.matchAny") },
          ]}
        />
      )}
      {props.terms.map((term, i) => (
        <span key={termKey(term)} className="hstack" style={{ gap: 6 }}>
          {i > 0 && <span className="chip b">{t(combine === "any" ? "geneSearch.or" : "geneSearch.and")}</span>}
          <RemovableChip
            tone={term.want === "absent" ? undefined : "g"}
            removeLabel={t("geneSearch.delete", { name: termLabel(t, term) })}
            onRemove={() => props.onChange(props.terms.filter((_, j) => j !== i))}
          >
            {termLabel(t, term)}
          </RemovableChip>
        </span>
      ))}
      <Popover opened={open} onChange={setOpen} position="bottom-start" withArrow trapFocus>
        <Popover.Target>
          <button type="button" className="chip gs-add" onClick={() => setOpen((o) => !o)}>
            {t("geneSearch.addTerm")}
          </button>
        </Popover.Target>
        <Popover.Dropdown>
          <div className="stack" style={{ gap: 8, width: 300 }}>
            <SegmentedControl
              size="xs"
              value={kind}
              onChange={(v) => {
                setKind(v as Term["kind"]);
                setName(null);
              }}
              data={KINDS.map((k) => ({ value: k, label: t(`geneSearch.kind.${k}`) }))}
            />
            <Select
              placeholder={hint("searchSelect")}
              size="xs"
              searchable
              label={t(`geneSearch.kind.${kind}`)}
              value={name}
              onChange={setName}
              data={names}
              limit={200}
            />
            <SegmentedControl
              size="xs"
              value={want}
              onChange={(v) => setWant(v as Term["want"])}
              data={WANTS.filter((w) => w !== "value" || kind === "locus").map((w) => ({ value: w, label: t(`geneSearch.want.${w}`) }))}
            />
            {want === "value" && (
              <TextInput
                size="xs"
                label={t("geneSearch.value")}
                placeholder="5+10"
                value={value}
                onChange={(e) => setValue(e.currentTarget.value)}
              />
            )}
            <Button size="xs" disabled={!ready} onClick={add}>
              {t("geneSearch.addCondition")}
            </Button>
          </div>
        </Popover.Dropdown>
      </Popover>
    </div>
  );
}
